import React from "react";
import { FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import classNames from 'classnames';
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";
import AddressSearchComponent, { Address } from "./AddressSearchComponent";
import { getRootComponent, findMatchedAddressElement, isBxpForm } from "../../utilities/ElementUtils";
import { getAddress, getCxpAddressString } from "../../utilities/addressUtils";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import { getEnvVar } from "../../config/RuntimeConfig";

export interface CXPAddress {
  addressLine1: string;
  addressLine2?: string | null;
  streetSuffix?: string;
  city: string;
  province: string;
  postalCode: string;
  countryCode: string;
  nocVersion?: string | null;
  isDetailAddress?: boolean;
};

interface Props {
  field: FormSectionElements;
  editable: boolean;
  title;
  labelControlArray;
  requiredField;
  callbackElement: (programContainer: ProgramContainer)=> void;
  isEng: boolean;
}


export interface ErrorMessage {
  id: string;
  message: string;
}

interface State {
  errors: ErrorMessage[];
  address?: Address;
}

export class AddressComponent extends React.Component<Props, State> {
  static REQUIRED_LABEL_ENGLISH = "Required"  
  static REQUIRED_LABEL_FRENCH = 'Obligatorie';
  profile = getRootComponent(this.props.field).Profile;

  constructor(props: Props) {
    super(props);
   
    this.state = {
      errors: [],
      address: undefined,
    };

    this.props.field.Touched=false;
    this.props.field.Valid=true;

    if (this.props.field.addressType === 'Mailing' && this.props.field.mailingSameAsResidential) {
      const residentialField = findMatchedAddressElement(this.props.field);
      if (!residentialField?.Address) {
        this.props.field.mailingSameAsResidential = false;
      }
    }
  }

  setFieldValue = (value: string) => {
    this.props.field.Value=value;
    const ignoreRequired = this.props.field.AlwaysDisplayCity && this.props.field.AlwaysDisplayProvince;
    if (this.props.field.Optional !== 'Y' && !ignoreRequired) {
      this.props.field.Valid = !!value;
    }
    this.props.field.Touched = true;
    this.props.field.IsEmpty = !value;
    if (value) {
      this.props.field.ErrorMessage = '';
      this.props.field.ErrorMessageFRA = '';
    }
  }

  setFieldValid = (valid: boolean) => {
    this.props.field.Valid = valid;
  }

  setValidationErrorMessage = (errors: ErrorMessage[]) => {
    const message = errors.reduce((sum, error) => {
      sum += error.message + '\n';
      return sum;
    }, '');
    this.props.field.ErrorMessage = message
    this.props.field.ErrorMessageFRA = message
  }

  setFieldAddress = (address: Address) => {
    console.log('setting this address in setFieldAddress',address);
    this.props.field.Address = address;
    // if (!this.props.field.addressType || this.props.field.addressType === 'Residential') { 
    //   const root = getRootComponent(this.props.field);
    //   root.Profile = {...root.Profile ?? {}, address: address};
    // }
  };

  setSameAsResidential = (value: boolean) => {
    this.props.field.mailingSameAsResidential = value;
  }

  getToken = () => {
    const root = getRootComponent(this.props.field);
    // console.log('address token', root.Profile?.token);
    return root.Profile?.token;
  }

  render() {
    let field = this.props.field;
    const getAddressLabel = (key: string, defaultEn: string, defaultFr: string) => {
      const labels = this.props.isEng ? field.addressFieldLabels : field.addressFieldLabelsFRA;
      return labels?.[key]?.trim() || (this.props.isEng ? defaultEn : defaultFr);
    }
    
    let displayfield: boolean = true;
    let editable = this.props.editable;
    let labelControlArray = this.props.labelControlArray;
    let requiredField = this.props.requiredField;
    let controlArray = [];
    let title = this.props.title;


    if (!field.Value && !field.Touched && (field.Address || getRootComponent(field).Profile?.address)) {
      field.Value = getAddress(field.Address) || getCxpAddressString(getRootComponent(field).Profile?.address);
    }

    if (field.Script) {
      FormValidationUtils.runJavascript(field);
      displayfield = field.Visible;  // Allows script to hide/show the element.
    }

 
    if (displayfield) {
      if (editable) {
        controlArray.push(<>
          <div className="ontario-form-group" key={'fdIpt_div_address_' + this.props.field.Id} style={{ zIndex: 10 }}>
            <div key={'fdLbl_div_' + this.props.field.Id} >
              {labelControlArray}
            </div>

            {(this.props.field.ErrorMessage || this.props.field.ErrorMessageFRA) && (
              <LiveValidationErrMsg
                field={this.props.field}
                isEng={this.props.isEng}
                key={"LiveValidationErrMsg_" + field.LoadBindName}
                id={`${field.LoadBindName}-${field.Id}`}
              />
            )}

            <div key={'fdIpt_div_search' + field.Id} >
              <AddressSearchComponent
                lang={this.props.isEng ? 'en' : 'fr'}
                fieldName="address"
                accessToken={this.getToken()}
                searchUrl={getEnvVar('PC_LOOKUP_URL') || this.props.field.AddressLookupUrl}
                setFieldValue={this.setFieldValue}
                setFieldValid={this.setFieldValid}
                setFieldAddress={this.setFieldAddress}
                setSameAsResidential={this.setSameAsResidential}
                setValidationErrorMessage={this.setValidationErrorMessage}
                field={this.props.field}
                callback={(element) => this.props.callbackElement(element)}
              />
            </div>
          </div>
       </>)
      } else {
        if(isBxpForm(this.props.field)){
          controlArray.push(
            <div className='pdf-three-column'>
            <div className='no-bottom-border'> {this.props.isEng ? "Address lookup" : "Recherche d'adresse"}</div>
              <div> {getAddressLabel('unitNumber', "Unit number", "Numéro d'unité")}</div>
              <div>{this.props.field.Address?.suiteNumber}</div>
            </div>
          );
          controlArray.push(
            <div className='pdf-three-column'>
              <div className='no-bottom-border'></div>
              <div> {getAddressLabel('streetNumber', "Street number", "Numéro de la rue")}</div>
              <div className={classNames({ 'invalid-field--highlighted': (!this.props.field.Address?.streetNumber) && requiredField })} >{this.props.field.Address?.streetNumber}</div>
            </div>
          );
          controlArray.push(
            <div className='pdf-three-column'>
              <div className='no-bottom-border'></div>
              <div> {getAddressLabel('streetName', "Street name", "Nom de la rue")}</div>
              <div className={classNames({ 'invalid-field--highlighted': (!this.props.field.Address?.streetName) && requiredField })} >{this.props.field.Address?.streetName}</div>
            </div>
          );
        if(!this.props.field.hideStreetType){
          controlArray.push(
            <div className='pdf-three-column'>
              <div className='no-bottom-border'></div>
              <div> {getAddressLabel('streetType', "Street Type", "Type de rue")}</div>
              <div>{this.props.field.Address?.streetType}</div>
            </div>
          );
        }
         if(!this.props.field.hideStreetDirection){
          controlArray.push(
            <div className='pdf-three-column'>
              <div className='no-bottom-border'></div>
              <div> {getAddressLabel('streetDirection', "Street Direction", "Direction de la rue")}</div>
              <div>{this.props.field.Address?.streetDirection}</div>
            </div>
          );}
          if(!this.props.field.hidePOBox){
            controlArray.push(
              <div className='pdf-three-column'>
                <div className='no-bottom-border'></div>
                <div> {getAddressLabel('poBoxNumber', "P.O.Box", "Case postale")}</div>
                <div>{this.props.field.Address?.poBoxNumber}</div>
              </div>
            );
          }
          if(!this.props.field.hideRuralRoute){
            controlArray.push(
              <div className='pdf-three-column'>
                <div className='no-bottom-border'></div>
                <div> {getAddressLabel('ruralRoute', "Rural route", "Route rurale")}</div>
                <div>{this.props.field.Address?.ruralRoute}</div>
              </div>
            );
          }
          controlArray.push(
            <div className='pdf-three-column'>
              <div className='no-bottom-border'></div>
              <div> {getAddressLabel('municipality', "City/Town", "Ville")}</div>
              <div className={classNames({ 'invalid-field--highlighted': (!this.props.field.Address?.municipality) && requiredField })} >{this.props.field.Address?.municipality}</div>
            </div>
          );
          controlArray.push(
            <div className='pdf-three-column'>
              <div className='no-bottom-border'></div>
              <div> {getAddressLabel('province', "Province", "Province")}</div>
              <div className={classNames({ 'invalid-field--highlighted': (!this.props.field.Address?.province) && requiredField })} >{this.props.field.Address?.province}</div>
            </div>
          );    

          if(this.props.field.isOtherOptional){  
          controlArray.push(
            <div className='pdf-three-column'>
              <div className={!this.props.field.isOtherOptional ? 'no-bottom-border' : ''}></div>
              <div> {getAddressLabel('provinceOther', "Other", "Autre")}</div>
              <div>{this.props.field.Address?.provinceOther}</div>
            </div>
          );  
        }

          controlArray.push(
            <div className='pdf-three-column'>
              <div className={!this.props.field.hideCountry ? 'no-bottom-border' : ''}></div>
              <div> {getAddressLabel('postalCode', "Postal code", "Code postal")}</div>
              <div className={classNames({ 'invalid-field--highlighted': (!this.props.field.Address?.postalCode) && requiredField })} >{this.props.field.Address?.postalCode}</div>
            </div>
          );  
          if(!this.props.field.hideCountry){
            controlArray.push(
              <div className='pdf-three-column'>
                <div></div>
                <div> {getAddressLabel('country', "Country", "Pays")}</div>
                <div className={classNames({ 'invalid-field--highlighted': (!this.props.field.Address?.country) && requiredField })} >{this.props.field.Address?.country}</div>
              </div>
            );              
          }   

        }else{
          controlArray.push(
            <div className='pdf-two-column'>
              <div>{title}</div>
              <div className={classNames({ 'invalid-field--highlighted': (this.props.field.Value == '' || this.props.field.Value == null) && requiredField })} >{this.props.field.Value}</div>
            </div>
          );
        }
      }
    }

    return controlArray;
  }
}
